import type { SVGProps } from "react"
import {
	IconArrowsLeftRightTrailOutline18,
	IconBookOpen4Outline18,
	IconChartBarTrendUpOutline18,
	IconChartTrendUpOutline18,
	IconChatBubbleClockOutline18,
} from "nucleo-ui-outline-18"

import type { CopilotScope } from "@productlane/lib/src/ai"
import type { IconType } from "@productlane/ui"
import { CheckSpellingIcon } from "@productlane/ui/src/icons/project/check-spelling-icon"
import { DraftResponseIcon } from "@productlane/ui/src/icons/project/draft-response-icon"
import { SummarizeIcon } from "@productlane/ui/src/icons/project/summarize-icon"

import type { ThreadQuickAction, ThreadView } from "./thread-quick-actions"
import { isAgentHome, isRecommendationsOverview } from "./copilot-empty-state"
import { threadQuickActions } from "./thread-quick-actions"

export const MAKE_IT_SHORTER_MESSAGE =
	"Make it shorter. Cut duplication and repeated points only, don't reword anything to a synonym or drop any information."

// Button draws its icons at 1.75; the project icons beside this one are drawn at 1.
const BookIcon: IconType = (props: SVGProps<SVGSVGElement>) => (
	<IconBookOpen4Outline18 {...props} strokeWidth={1} />
)

const TrendIcon: IconType = (props: SVGProps<SVGSVGElement>) => (
	<IconChartTrendUpOutline18 {...props} strokeWidth={1} />
)
const HandoffIcon: IconType = (props: SVGProps<SVGSVGElement>) => (
	<IconArrowsLeftRightTrailOutline18 {...props} strokeWidth={1} />
)

// Shared with the inbox empty state's suggestion buttons.
export const WAITING_ON_US_PROMPT =
	"Which threads are waiting on a reply from us?"
export const TOP_REQUESTS_PROMPT =
	"What are customers requesting most right now?"

const WaitingOnUsIcon: IconType = (props: SVGProps<SVGSVGElement>) => (
	<IconChatBubbleClockOutline18 {...props} strokeWidth={1} />
)
const TopRequestsIcon: IconType = (props: SVGProps<SVGSVGElement>) => (
	<IconChartBarTrendUpOutline18 {...props} strokeWidth={1} />
)

const WORKSPACE_ACTIONS: readonly ThreadQuickAction[] = [
	{
		label: "Waiting on us",
		Icon: WaitingOnUsIcon,
		message: WAITING_ON_US_PROMPT,
	},
	{
		label: "Top requests",
		Icon: TopRequestsIcon,
		message: TOP_REQUESTS_PROMPT,
	},
]

const WRITING_ACTIONS: readonly ThreadQuickAction[] = [
	{ label: "Improve the writing", Icon: DraftResponseIcon },
	{ label: "Fix spelling and grammar", Icon: CheckSpellingIcon },
	{
		label: "Make it shorter",
		Icon: SummarizeIcon,
		message: MAKE_IT_SHORTER_MESSAGE,
	},
]

const AGENT_ACTIONS: readonly ThreadQuickAction[] = [
	{
		label: "Make the change shorter",
		Icon: SummarizeIcon,
		message: MAKE_IT_SHORTER_MESSAGE,
	},
	{ label: "Improve the writing", Icon: DraftResponseIcon },
	{ label: "Why does this help?", Icon: BookIcon },
]

const RECOMMENDATIONS_ACTIONS: readonly ThreadQuickAction[] = [
	{
		label: "Which one first?",
		Icon: TrendIcon,
		message:
			"Which recommendation should I accept first to resolve the most conversations, and why?",
	},
	{
		label: "Why do chats get handed off?",
		Icon: HandoffIcon,
		message:
			"Why does the agent hand conversations to the team most often in the last 30 days?",
	},
	{
		label: "What do the docs miss?",
		Icon: BookIcon,
		message:
			"Which customer questions does the agent miss because the docs don't answer them?",
	},
]

const REPORTING_ACTIONS: readonly ThreadQuickAction[] = [
	{ label: "Recommend improvements", Icon: TrendIcon },
	{ label: "What is the agent missing?", Icon: BookIcon },
	{ label: "Why did it escalate?", Icon: HandoffIcon },
]

export function copilotQuickActions(
	scope: CopilotScope | null | undefined,
	view: ThreadView | null,
): readonly ThreadQuickAction[] {
	switch (scope?.kind) {
		case "article":
		case "changelog":
			return WRITING_ACTIONS
		case "reporting":
			return REPORTING_ACTIONS
		case "agent":
			if (isAgentHome(scope)) return []
			return isRecommendationsOverview(scope)
				? RECOMMENDATIONS_ACTIONS
				: AGENT_ACTIONS
		case "workspace":
			return WORKSPACE_ACTIONS
		default:
			return threadQuickActions(view)
	}
}
